/**
 * Utility functions for working with date strings in YYYY-MM-DD format
 * This avoids timezone conversion issues by working with strings directly
 */

import type { DateString } from '~/types';

/**
 * Validates if a string is a valid YYYY-MM-DD date
 */
export const isValidDateString = (dateString: string): dateString is DateString => {
  if (!dateString) return false;

  // Check format
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(dateString)) return false;

  // Parse the date components manually to avoid timezone issues
  const [year, month, day] = dateString.split('-').map(Number);

  // Basic range validation
  if (year < 1000 || year > 9999) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  // Check days in month (simplified - doesn't handle leap years perfectly, but good enough)
  const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (day > daysInMonth[month - 1]) return false;

  // Additional leap year check for February
  if (month === 2 && day === 29) {
    const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    if (!isLeapYear) return false;
  }

  return true;
};

/**
 * Converts MM/DD/YYYY to YYYY-MM-DD format
 */
export const convertToDateString = (mmddyyyy: string): string => {
  if (!mmddyyyy) return '';

  const parts = mmddyyyy.split('/');
  if (parts.length !== 3) return '';

  const [month, day, year] = parts;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

/**
 * Converts YYYY-MM-DD to MM/DD/YYYY format for display
 */
export const formatDateForDisplay = (dateString: string): string => {
  if (!dateString || !isValidDateString(dateString)) return '';

  const [year, month, day] = dateString.split('-');
  return `${parseInt(month, 10)}/${parseInt(day, 10)}/${year}`;
};

/**
 * Gets the current date in YYYY-MM-DD format
 */
export const getCurrentDateString = (): string => {
  const now = new Date();
  return now.toISOString().split('T')[0];
};

/**
 * Adds days to a date string
 */
export const addDays = (dateString: string, days: number): string => {
  if (!isValidDateString(dateString)) return '';

  // Use UTC methods to avoid timezone issues
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day)); // month is 0-indexed in Date constructor
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().split('T')[0];
};

/**
 * Adds months to a date string
 */
export const addMonths = (dateString: string, months: number): string => {
  if (!isValidDateString(dateString)) return '';

  // Use UTC methods to avoid timezone issues
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day)); // month is 0-indexed in Date constructor
  date.setUTCMonth(date.getUTCMonth() + months);
  return date.toISOString().split('T')[0];
};

/**
 * Adds years to a date string
 */
export const addYears = (dateString: string, years: number): string => {
  if (!isValidDateString(dateString)) return '';

  // Use UTC methods to avoid timezone issues
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day)); // month is 0-indexed in Date constructor
  date.setUTCFullYear(date.getUTCFullYear() + years);
  return date.toISOString().split('T')[0];
};

/**
 * Compares two date strings
 * Returns: -1 if date1 < date2, 0 if equal, 1 if date1 > date2
 */
export const compareDates = (date1: string, date2: string): number => {
  if (!isValidDateString(date1) || !isValidDateString(date2)) {
    throw new Error('Invalid date strings for comparison');
  }

  if (date1 < date2) return -1;
  if (date1 > date2) return 1;
  return 0;
};

/**
 * Checks if a date is within a range
 */
export const isDateInRange = (dateString: string, startDate: string, endDate: string): boolean => {
  return compareDates(dateString, startDate) >= 0 && compareDates(dateString, endDate) <= 0;
};

/**
 * Gets the last day of a month in YYYY-MM-DD format
 */
export const getLastDayOfMonth = (year: number, month: number): string => {
  // Use UTC methods to avoid timezone issues
  const date = new Date(Date.UTC(year, month, 0)); // month is 1-indexed, so this gets last day of previous month
  return date.toISOString().split('T')[0];
};

/**
 * Gets the year from a date string
 */
export const getYear = (dateString: string): number => {
  if (!isValidDateString(dateString)) {
    throw new Error('Invalid date string for getYear');
  }
  return parseInt(dateString.split('-')[0], 10);
};

/**
 * Formats a date string for table display (e.g., "Jan 15, 2024")
 */
export const formatDateForTable = (dateString: string): string => {
  if (!dateString || !isValidDateString(dateString)) return '';

  const [year, month, day] = dateString.split('-').map(Number);
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  return `${monthNames[month - 1]} ${day}, ${year}`;
};

// Cached formatter for EST timezone - created once for performance
const EST_DATETIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  year: '2-digit',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false, // 24-hour format
});

/**
 * Extracts date/time parts from a Date using EST timezone formatter
 */
const getEstDateTimeParts = (date: Date) => {
  const parts = EST_DATETIME_FORMATTER.formatToParts(date);
  return {
    year: parts.find((p) => p.type === 'year')?.value || '00',
    month: parts.find((p) => p.type === 'month')?.value || '01',
    day: parts.find((p) => p.type === 'day')?.value || '01',
    hours: parts.find((p) => p.type === 'hour')?.value || '00',
    minutes: parts.find((p) => p.type === 'minute')?.value || '00',
  };
};

/**
 * Generates the base configuration name from date parts (YYMMDD.HHMM format)
 */
const getBaseConfigName = (parts: ReturnType<typeof getEstDateTimeParts>): string => {
  return `${parts.year}${parts.month}${parts.day}.${parts.hours}${parts.minutes}`;
};

/**
 * Generates a default configuration name in format: YYMMDD.HHMM or YYMMDD.HHMM.[n]
 * Uses EST timezone (America/New_York) and 24-hour format
 * Only includes .[n] suffix if there are other configurations created in the same minute
 * @param _orderId - Unused, kept for API compatibility (suffix is based on same-minute count)
 * @param date - Optional date to use for timestamp (defaults to current time)
 * @param existingConfigurations - Optional array of existing configurations to check for duplicates
 * @returns Configuration name string (e.g., "250130.1430" or "250130.1430.2")
 */
export const generateConfigurationName = (
  _orderId: number,
  date?: Date | string,
  existingConfigurations?: Array<{ createdAt?: string; name?: string }>
): string => {
  // Use provided date or current time
  const dateToUse = date ? (typeof date === 'string' ? new Date(date) : date) : new Date();
  const parts = getEstDateTimeParts(dateToUse);
  const baseName = getBaseConfigName(parts);

  // Check if there are other configurations created in the same minute
  if (existingConfigurations && existingConfigurations.length > 0) {
    const sameMinuteCount = existingConfigurations.filter((config) => {
      if (!config.createdAt) return false;
      const configParts = getEstDateTimeParts(new Date(config.createdAt));
      return getBaseConfigName(configParts) === baseName;
    }).length;

    // If there are configurations created in the same minute, append count-based suffix
    if (sameMinuteCount > 0) {
      return `${baseName}.${sameMinuteCount + 1}`;
    }
  }

  // No duplicates in the same minute, return base name without suffix
  return baseName;
};
